import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/utils';

/**
 * `<PageContainer>` — parity-port layout primitive (bundle 117).
 *
 * Generic wrapper for top-level page bodies. Caps content width with a
 * `max-w-*` token, applies symmetric padding on each axis, and centers
 * horizontally by default. All knobs are props-driven so consumers don't
 * need to repeat Tailwind utility strings across pages.
 *
 * Rendered with `data-testid="page-container"` and a `data-size` attribute
 * so tests can assert the selected size without scraping classNames.
 *
 * #1701 — `'wide'` (1200px) added to match the de-facto standard pages
 * had been hand-rolling (`mx-auto max-w-[1200px]`). `'none'` paddings
 * added so consumers inside an already-padded `<main>` shell can opt
 * out of the outer gutter without re-implementing the wrapper.
 *
 * #1701 (asChild) — `asChild` prop added so pages that want to keep
 * their semantic wrapper element (`<main>`, `<section>`, `<article>`)
 * can render the container classes onto their own element via Radix
 * `Slot`, mirroring the `Card` / `Button` primitive contract. Without
 * this, adopting the primitive on a page whose root is a landmark
 * element would silently demote it to a `<div>`. When `asChild` is
 * true, the caller passes exactly one child element and the container
 * classes + `data-testid` + `data-size` are forwarded onto it.
 */

export type PageContainerSize =
  | 'sm'
  | 'md'
  | 'reading'
  | 'lg'
  | 'xl'
  | 'wide'
  | 'full';
export type PageContainerPadding = 'none' | 'tight' | 'normal' | 'loose';

export interface PageContainerProps {
  children: React.ReactNode;
  /** Max width token. Defaults to `'lg'` (`max-w-4xl`). */
  size?: PageContainerSize;
  /**
   * Horizontal padding intensity. Defaults to `'normal'` (`px-4`).
   * Pass `'none'` when nested inside an already-padded shell to avoid
   * stacking gutters.
   */
  paddingX?: PageContainerPadding;
  /** Vertical padding intensity. Defaults to `'normal'` (`py-4`). */
  paddingY?: PageContainerPadding;
  /** Apply `mx-auto` to center horizontally. Defaults to `true`. */
  centered?: boolean;
  /**
   * When true, forward the container's classes + data attributes onto
   * the (single) child element via Radix `Slot` instead of wrapping in
   * a `<div>`. Use to keep a semantic landmark (`<main>` / `<section>`
   * / `<article>`) as the actual DOM element. Defaults to `false`.
   */
  asChild?: boolean;
  className?: string;
}

const SIZE_CLASSES: Record<PageContainerSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-2xl',
  /**
   * #1701 — `'reading'` (`max-w-3xl` / 768px) sits between `md` (2xl)
   * and `lg` (4xl). Named for the common use case — long-form form
   * columns, single-column public-facing pages (contact / LP portal
   * / building-create wizard) where a wider `lg` would let the eye
   * scan too far and a narrower `md` would clip inline hint text.
   */
  reading: 'max-w-3xl',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  /**
   * #1701 — `'wide'` is the canonical 1200px container the audit
   * found pages hand-rolling. Pinned literal (not a Tailwind named
   * size) because Tailwind's 6xl is 1152px and 7xl is 1280px; the
   * de-facto Senviro standard sits between them.
   */
  wide: 'max-w-[1200px]',
  full: 'max-w-full',
};

const PADDING_X_CLASSES: Record<PageContainerPadding, string> = {
  none: '',
  tight: 'px-2',
  normal: 'px-4',
  loose: 'px-8',
};

const PADDING_Y_CLASSES: Record<PageContainerPadding, string> = {
  none: '',
  tight: 'py-2',
  normal: 'py-4',
  loose: 'py-8',
};

export function PageContainer({
  children,
  size = 'lg',
  paddingX = 'normal',
  paddingY = 'normal',
  centered = true,
  asChild = false,
  className,
}: PageContainerProps): JSX.Element {
  const Comp = asChild ? Slot : 'div';
  return (
    <Comp
      data-testid="page-container"
      data-size={size}
      className={cn(
        SIZE_CLASSES[size],
        PADDING_X_CLASSES[paddingX],
        PADDING_Y_CLASSES[paddingY],
        centered && 'mx-auto',
        className,
      )}
    >
      {children}
    </Comp>
  );
}
